import { useState, useEffect } from "react";
import { api } from "../App";
import { toast } from "sonner";
import { 
  Settings as SettingsIcon, 
  Plus,
  Edit3,
  Trash2,
  ChevronDown,
  ChevronRight,
  GripVertical,
  Check,
  X,
  RefreshCw
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

const FIELD_LABELS = {
  category: "Categories",
  importance: "Importance Levels",
  status: "Status Options",
  urgency: "Urgency Levels",
  signal: "Signal Types",
  audience: "Audience Types"
};

const FIELD_ORDER = ["category", "importance", "status", "urgency", "signal", "audience"];

// Option Form Dialog
const OptionFormDialog = ({ option, fieldName, open, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    field_name: fieldName || "",
    value: "",
    label: "",
    description: "",
    sort_order: 0,
    is_active: true
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (option) {
      setFormData({
        field_name: option.field_name || fieldName,
        value: option.value || "",
        label: option.label || "",
        description: option.description || "",
        sort_order: option.sort_order || 0,
        is_active: option.is_active !== false
      });
    } else {
      setFormData({
        field_name: fieldName || "",
        value: "",
        label: "",
        description: "",
        sort_order: 99,
        is_active: true
      });
    }
  }, [option, fieldName]);

  const handleSave = async () => {
    if (!formData.value.trim() || !formData.label.trim()) {
      toast.error("Value and Label are required");
      return;
    }

    setSaving(true);
    try {
      if (option?.id) {
        await api.patch(`/taxonomy/${option.id}`, {
          label: formData.label,
          description: formData.description,
          sort_order: formData.sort_order,
          is_active: formData.is_active
        });
        toast.success("Option updated");
      } else {
        await api.post('/taxonomy', formData);
        toast.success("Option created");
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save option");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#12121a] border-[#2a2a3a] max-w-md" data-testid="option-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {option ? "Edit Option" : "Add Option"}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <Label className="font-mono text-[10px] uppercase text-[#7a7a8e]">Value (internal key) *</Label>
            <Input
              value={formData.value}
              onChange={(e) => setFormData({...formData, value: e.target.value.toLowerCase().replace(/\s+/g, '_')})}
              placeholder="e.g., defi, high, breaking"
              className="mt-1.5 bg-[#1a1a26] border-[#2a2a3a]"
              disabled={!!option}
              data-testid="option-value"
            />
            {!!option && <p className="text-[10px] text-[#7a7a8e] mt-1">Value cannot be changed after creation</p>}
          </div>

          <div>
            <Label className="font-mono text-[10px] uppercase text-[#7a7a8e]">Label (display name) *</Label>
            <Input
              value={formData.label}
              onChange={(e) => setFormData({...formData, label: e.target.value})}
              placeholder="e.g., DeFi, High Priority, Breaking News"
              className="mt-1.5 bg-[#1a1a26] border-[#2a2a3a]"
              data-testid="option-label"
            />
          </div>

          <div>
            <Label className="font-mono text-[10px] uppercase text-[#7a7a8e]">Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Explain what this option means (used by AI and shown as tooltips)"
              className="mt-1.5 bg-[#1a1a26] border-[#2a2a3a] min-h-[80px]"
              data-testid="option-description"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="font-mono text-[10px] uppercase text-[#7a7a8e]">Sort Order</Label>
              <Input
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({...formData, sort_order: parseInt(e.target.value) || 0})}
                className="mt-1.5 bg-[#1a1a26] border-[#2a2a3a]"
                data-testid="option-sort-order"
              />
            </div>
            <div className="flex items-end pb-2">
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(v) => setFormData({...formData, is_active: v})}
                  data-testid="option-active"
                />
                <Label className="font-mono text-xs text-[#7a7a8e]">Active</Label>
              </div>
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-[#7a7a8e]">Cancel</Button>
          <Button 
            onClick={handleSave} 
            disabled={saving} 
            className="bg-[#6ee7b7] text-[#0a0a0f] hover:bg-[#6ee7b7]/90"
            data-testid="option-save"
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Taxonomy Field Group
const TaxonomyFieldGroup = ({ fieldName, options, onEdit, onToggleActive, onRefresh }) => {
  const [isOpen, setIsOpen] = useState(fieldName === "category");

  return (
    <Card className="bg-[#12121a] border-[#2a2a3a]">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-[#1a1a26] transition-colors py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {isOpen ? <ChevronDown className="w-4 h-4 text-[#7a7a8e]" /> : <ChevronRight className="w-4 h-4 text-[#7a7a8e]" />}
                <CardTitle className="font-heading text-base">
                  {FIELD_LABELS[fieldName] || fieldName}
                </CardTitle>
                <Badge className="font-mono text-[10px] bg-[#1a1a26] text-[#7a7a8e] border-[#2a2a3a]">
                  {options.length} options
                </Badge>
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <CardContent className="pt-0">
            <div className="space-y-2">
              {options.map((option, idx) => (
                <div 
                  key={option.id} 
                  className={`flex items-center gap-3 p-3 rounded border ${
                    option.is_active 
                      ? 'bg-[#1a1a26] border-[#2a2a3a]' 
                      : 'bg-[#1a1a26]/50 border-[#2a2a3a]/50 opacity-60'
                  }`}
                  data-testid={`option-${option.id}`}
                >
                  <GripVertical className="w-4 h-4 text-[#7a7a8e]/50 cursor-grab" />
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-xs text-[#6ee7b7]">{option.value}</span>
                      <span className="text-sm text-[#e0e0e8]">{option.label}</span>
                      {!option.is_active && (
                        <Badge className="font-mono text-[10px] bg-[#7a7a8e]/20 text-[#7a7a8e] border-[#7a7a8e]/30">
                          Inactive
                        </Badge>
                      )}
                    </div>
                    {option.description && (
                      <p className="text-xs text-[#7a7a8e] line-clamp-1">{option.description}</p>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onToggleActive(option)}
                      className={`h-7 w-7 p-0 ${option.is_active ? 'text-[#7a7a8e] hover:text-[#ef4444]' : 'text-[#7a7a8e] hover:text-[#6ee7b7]'}`}
                      title={option.is_active ? 'Deactivate' : 'Activate'}
                      data-testid={`toggle-${option.id}`}
                    >
                      {option.is_active ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onEdit(option)}
                      className="h-7 w-7 p-0 text-[#7a7a8e] hover:text-[#60a5fa]"
                      title="Edit"
                      data-testid={`edit-${option.id}`}
                    >
                      <Edit3 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit({ field_name: fieldName })}
              className="mt-3 text-[#6ee7b7] hover:text-[#6ee7b7] hover:bg-[#6ee7b7]/10"
              data-testid={`add-${fieldName}`}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add {FIELD_LABELS[fieldName]?.replace(/s$/, '') || 'Option'}
            </Button>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

const SettingsPage = () => {
  const [taxonomy, setTaxonomy] = useState({});
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [editOption, setEditOption] = useState(null);
  const [editFieldName, setEditFieldName] = useState(null);

  const fetchTaxonomy = async () => {
    setLoading(true);
    try {
      const response = await api.get('/taxonomy');
      setTaxonomy(response.data);
    } catch (error) {
      toast.error("Failed to load taxonomy");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTaxonomy();
  }, []);

  const handleSeedData = async () => {
    setSeeding(true);
    try {
      const response = await api.post('/taxonomy/seed');
      toast.success(`Seeded ${response.data.inserted} options (${response.data.skipped} already existed)`);
      fetchTaxonomy();
    } catch (error) {
      toast.error("Failed to seed taxonomy");
    } finally {
      setSeeding(false);
    }
  };

  const handleToggleActive = async (option) => {
    try {
      await api.patch(`/taxonomy/${option.id}`, { is_active: !option.is_active });
      toast.success(`Option ${option.is_active ? 'deactivated' : 'activated'}`);
      fetchTaxonomy();
    } catch (error) {
      toast.error("Failed to update option");
    }
  };

  const handleEdit = (option) => {
    if (option.id) {
      setEditOption(option);
      setEditFieldName(option.field_name);
    } else {
      setEditOption(null);
      setEditFieldName(option.field_name);
    }
  };

  const isEmpty = Object.keys(taxonomy).length === 0;

  return (
    <div data-testid="settings-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-semibold">Settings</h1>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={fetchTaxonomy}
            className="border-[#2a2a3a] text-[#7a7a8e] hover:text-[#e0e0e8]"
            data-testid="refresh-taxonomy"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          {isEmpty && (
            <Button
              onClick={handleSeedData}
              disabled={seeding}
              className="bg-[#6ee7b7] hover:bg-[#6ee7b7]/90 text-[#0a0a0f] font-semibold"
              data-testid="seed-taxonomy"
            >
              {seeding ? "Seeding..." : "Seed Default Values"}
            </Button>
          )}
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-[#12121a] border-[#2a2a3a] mb-6">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <SettingsIcon className="w-5 h-5 text-[#a78bfa] mt-0.5" />
            <div>
              <h3 className="font-heading text-sm mb-1">Taxonomy Configuration</h3>
              <p className="text-xs text-[#7a7a8e]">
                Manage the dropdown options used throughout the platform. Changes here will update all filters and forms. 
                Deactivating an option hides it from new selections but preserves existing data.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-[#7a7a8e]">Loading taxonomy...</div>
        </div>
      ) : isEmpty ? (
        <Card className="bg-[#12121a] border-[#2a2a3a]">
          <CardContent className="p-8 text-center">
            <SettingsIcon className="w-8 h-8 text-[#7a7a8e] mx-auto mb-3" />
            <p className="text-[#7a7a8e] mb-2">No taxonomy options configured</p>
            <p className="text-[#7a7a8e]/60 text-sm mb-4">Click "Seed Default Values" to populate with initial data</p>
            <Button
              onClick={handleSeedData}
              disabled={seeding}
              className="bg-[#6ee7b7] hover:bg-[#6ee7b7]/90 text-[#0a0a0f]"
            >
              {seeding ? "Seeding..." : "Seed Default Values"}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {FIELD_ORDER.map(fieldName => (
            taxonomy[fieldName] && (
              <TaxonomyFieldGroup
                key={fieldName}
                fieldName={fieldName}
                options={taxonomy[fieldName]}
                onEdit={handleEdit}
                onToggleActive={handleToggleActive}
                onRefresh={fetchTaxonomy}
              />
            )
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <OptionFormDialog
        option={editOption}
        fieldName={editFieldName}
        open={editOption !== null || editFieldName !== null}
        onClose={() => {
          setEditOption(null);
          setEditFieldName(null);
        }}
        onSave={fetchTaxonomy}
      />
    </div>
  );
};

export default SettingsPage;
